import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Search } from "lucide-react";

/**
 * Reusable search dropdown with debounced API-backed options.
 *
 * Props:
 * - placeholder: string (button text when nothing selected)
 * - selectedLabel: string | null (current selected label)
 * - onSelect: (option: { label, value }) => void
 * - fetchOptions: (search: string) => Promise<Array<{ label: string; value: any }>>
 * - disabled?: boolean
 * - className?: object with keys: wrapper, button, dropdown, searchWrapper, searchInput, searchIcon, options, option, noResults
 */
const SearchDropdown = ({
  placeholder,
  selectedLabel,
  onSelect,
  fetchOptions,
  disabled = false,
  className = {},
}) => {
  const {
    wrapper = "",
    button = "",
    dropdown = "",
    searchWrapper = "",
    searchInput = "",
    searchIcon = "",
    options: optionsClassName = "",
    option = "",
    noResults = "",
  } = className;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const loadOptions = async (query) => {
    try {
      setLoading(true);
      const result = await fetchOptions(query || "");
      setOptions(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("SearchDropdown fetchOptions error:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Load initial options when opening
  useEffect(() => {
    if (open) {
      setSearch("");
      loadOptions("");
    }
  }, [open]);

  const handleSearchChange = (value) => {
    setSearch(value);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      loadOptions(value);
    }, 600);
  };

  const handleOptionClick = (option) => {
    if (!option) return;
    if (typeof onSelect === "function") {
      onSelect(option);
    }
    setOpen(false);
  };

  const label =
    selectedLabel && selectedLabel.trim().length > 0
      ? selectedLabel
      : placeholder;

  return (
    <div ref={wrapperRef} className={wrapper}>
      <button
        type="button"
        className={button}
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
          }
        }}
        disabled={disabled}
      >
        {label}{" "}
        {open ? <ChevronUp size={16} color="#6b7280" /> : <ChevronDown size={16} color="#6b7280" />}
      </button>

      {open && (
        <div className={dropdown}>
          <div className={searchWrapper}>
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={placeholder}
              className={searchInput}
            />
            <Search
              size={16}
              color="#6B7280"
              className={searchIcon}
            />
          </div>
          <div className={optionsClassName}>
            {loading ? (
              <div className={noResults}>Loading...</div>
            ) : options.length > 0 ? (
              options.map((opt) => (
                <div
                  key={opt.value}
                  className={option}
                  onClick={() => handleOptionClick(opt)}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className={noResults}>No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;


